import { useCheckbox, UseCheckboxProps } from '@chakra-ui/checkbox';
import {
  chakra,
  forwardRef,
  omitThemingProps,
  PropsOf,
  ThemingProps,
} from '@chakra-ui/system';
import { cx, dataAttr } from '@chakra-ui/utils';
import * as React from 'react';

type Omitted = 'defaultChecked' | 'checked' | 'onChange';

interface TextSwitchProps {
  offLabel: string;
  onLabel: string;
}

export interface SwitchProps
  extends Omit<UseCheckboxProps, 'isIndeterminate'>,
    Omit<PropsOf<typeof chakra.label>, Omitted>,
    TextSwitchProps,
    ThemingProps {}

const SlidingRadio = forwardRef<SwitchProps, 'input'>((props, ref) => {
  const ownProps = omitThemingProps(props);
  const { state, getInputProps, getCheckboxProps, htmlProps } = useCheckbox(
    ownProps
  );

  const inputProps = getInputProps({}, ref);
  const checkboxProps = getCheckboxProps();

  const labelStyles = {
    display: 'inline-block',
    verticalAlign: 'middle',
    lineHeight: 'normal',
  };

  return (
    <chakra.label
      {...htmlProps}
      className={cx('chakra-switch', props.className)}
      __css={labelStyles}
    >
      <input className="chakra-switch__input" {...inputProps} />
      <chakra.span
        {...checkboxProps}
        className="chakra-switch__track"
        borderRadius="full"
        position="relative"
        transition="all 120ms"
        bg="#ffffff00"
        w="400px"
        h="1.875rem"
        border="solid 1px"
        borderColor="gray.400"
        alignItems="center"
        display="inline-flex"
        flexShrink={0}
        justifyContent="flex-start"
        boxSizing="content-box"
        cursor="pointer"
        _disabled={{
          opacity: 0.4,
          cursor: 'not-allowed',
        }}
      >
        <chakra.span
          bg="white"
          transition="transform 250ms"
          borderRadius="9999px"
          transform="translateX(0)"
          width="200px"
          height="2rem"
          className="chakra-switch__thumb"
          _checked={{
            transform: 'translateX(200px)',
          }}
          data-checked={dataAttr(state.isChecked)}
          data-hover={dataAttr(state.isHovered)}
        />

        <chakra.label
          className="chakra-label__left"
          transition="color 250ms"
          position="absolute"
          right="25%"
          transform="translate(50%, 0);"
          pointerEvents="none"
          fontSize="14px"
          fontWeight="500"
          color="#A2A5B4"
          _checked={{
            color: 'blue.600',
          }}
          data-checked={dataAttr(state.isChecked)}
        >
          {props.offLabel}
        </chakra.label>

        <chakra.label
          className="chakra-label__right"
          transition="color 250ms"
          position="absolute"
          left="25%"
          transform="translate(-50%, 0);"
          pointerEvents="none"
          fontWeight="500"
          fontSize="14px"
          color="blue.600"
          _checked={{
            color: '#A2A5B4',
          }}
          data-checked={dataAttr(state.isChecked)}
        >
          {props.onLabel}
        </chakra.label>
      </chakra.span>
    </chakra.label>
  );
});

export default SlidingRadio;
